import { BhImage } from 'models/BhImage';
import { BhImageType } from 'models/BhImageType';
import { isEmpty } from './isEmpty';
import { ProductPageModel } from 'models/ProductPageModel';
import { isImage, isMediaWidget } from '../views/ProductPage/selector';

const DEFAULT_QUALITY = 40;
const RETINA_QUALITY = 30;

const getImgixname = (name: string, guid: string): string => {
  const index = name.lastIndexOf('.');
  if (index !== -1) {
    return `${name.substr(0, index)}__${guid}${name.substr(index)}`;
  } else {
    return `${name}__${guid}`;
  }
};

interface ImageDimension {
  width: number;
  height: number;
}

interface ImagesUrlsAsArrayProps extends BhImage {
  product: ProductPageModel['product'];
  cdnUrl: string;
  dimensions: ImageDimension;
  desktopDimensions: ImageDimension;
  hugeDimensions: ImageDimension;
  dpr?: number;
  quality?: number;
}

interface ImageProps extends ImagesUrlsAsArrayProps {
  documentId: string;
  documentName: string;
  media: BhImage;
}

export function fileUrl({
  documentId,
  documentName,
  cdnUrl
}: Pick<ImageProps, 'documentId' | 'documentName' | 'cdnUrl'>): string {
  if (!isEmpty(cdnUrl)) {
    const name = getImgixname(documentName, documentId);
    return `${cdnUrl}/pfiles/${name}`;
  }
  return `${cdnUrl}/files/${documentId}/${documentName}`;
}

const setImageUrl =
  ({ cdnUrl }: Pick<ImageProps, 'cdnUrl'>) =>
  ({
    media,
    dimensions,
    dpr,
    quality
  }: Pick<ImageProps, 'media' | 'dimensions' | 'dpr' | 'quality'>) =>
    imageUrl({
      ...media,
      cdnUrl,
      width: dimensions.width,
      height: dimensions.height,
      dpr,
      quality
    });

export function imagesUrlsAsArray({
  product,
  dimensions,
  desktopDimensions,
  hugeDimensions,
  cdnUrl
}: Pick<
  ImagesUrlsAsArrayProps,
  'product' | 'dimensions' | 'desktopDimensions' | 'hugeDimensions' | 'cdnUrl'
>) {
  const getImageUrl = setImageUrl({ cdnUrl });

  return product.widgets?.filter(isMediaWidget).map(widget =>
    widget.media
      .filter(isImage)
      .map(media => [
        getImageUrl({ media, dimensions, dpr: 2, quality: 30 }),
        getImageUrl({ media, dimensions: desktopDimensions, dpr: 1, quality: 40 }),
        getImageUrl({ media, dimensions: hugeDimensions, dpr: 1, quality: 40 })
      ])
      .reduce((prev: string[], curr: string[]) => [...prev, ...curr], [])
  )[0];
}

interface ImageUrlProps extends ImageProps {
  cdnUrl: string;
  width: number;
  height: number;
  dpr?: number;
  quality?: number;
}

export function imageUrl({
  type,
  id,
  name,
  cdnUrl,
  width,
  height,
  dpr = 1,
  quality = dpr > 1 ? RETINA_QUALITY : DEFAULT_QUALITY,
  isFromPia
}: Pick<
  ImageUrlProps,
  'type' | 'id' | 'name' | 'cdnUrl' | 'width' | 'height' | 'dpr' | 'quality' | 'isFromPia'
>): string {
  switch (type) {
    case BhImageType.Category:
    case BhImageType.Product:
      if (isFromPia) {
        return `${cdnUrl}/${id}/${name}?auto=format,compress&w=${width}&h=${height}&dpr=${dpr}&q=${quality}`;
      }
      if (!isEmpty(cdnUrl)) {
        const imgixname = getImgixname(name, id);
        return `${cdnUrl}/pimages/${imgixname}?auto=format,compress&w=${width}&h=${height}&dpr=${dpr}&q=${quality}`;
      }
      return '';
    case BhImageType.Banner:
      if (!isEmpty(cdnUrl)) {
        if (id.includes('__')) {
          return `${cdnUrl}/campimg/${id}?auto=format,compress&w=${width}&h=${height}&dpr=${dpr}&q=${quality}`;
        }

        return `${cdnUrl}/campimg/${getImgixname(
          name,
          id
        )}?auto=format,compress&w=${width}&h=${height}&dpr=${dpr}&q=${quality}`;
      }
      break;
    case BhImageType.CmsImage:
      return `${cdnUrl}/cmsmedia/media/${id}/${name}?auto=format,compress&fit=crop&w=${width}&h=${height}&dpr=${dpr}&q=${quality}`;
  }
  return '';
}
